import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Analytics } from '@vercel/analytics/react';
import { AuthProvider } from './contexts/AuthContext';
import { ModuleProvider } from './contexts/ModuleContext';
import { UserProvider } from './contexts/UserContext';
import { NotificationProvider } from './contexts/NotificationContext';
import Header from './components/Header';
import Dashboard from './components/Dashboard';
import TrainingModule from './components/TrainingModule';
import Quiz from './components/Quiz';
import Progress from './components/Progress';
import Login from './components/Login';
import AdminDashboard from './components/AdminDashboard';
import TrainingCalendar from './components/TrainingCalendar';
import Notifications from './components/Notifications';
import DefaultRoute from './components/DefaultRoute';
import ProtectedRoute from './components/ProtectedRoute';
import Footer from './components/Footer';
import WelcomeScreen from './components/WelcomeScreen';
import PrescreeningLevel from './components/PrescreeningLevel';
import PrescreeningCategory from './components/PrescreeningCategory';
import PrescreeningTags from './components/PrescreeningTags';
import './App.css';

function App() {
  // Right-click and keyboard shortcuts are now enabled
  // Removed code that was disabling context menu and developer tools

  return (
    <AuthProvider>
      <UserProvider>
        <ModuleProvider>
          <NotificationProvider>
            <Router>
              <AppContent />
            </Router>
          </NotificationProvider>
        </ModuleProvider>
      </UserProvider>
    </AuthProvider>
  );
}

function AppContent() {
  const location = useLocation();
  const isLoginPage = location.pathname === '/login' || location.pathname === '/login/';
  const shouldShowHeader = !isLoginPage;

  return (
    <div className="App">
      {shouldShowHeader && <Header />}
      <main className="main-content">
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route 
            path="/" 
            element={
              <ProtectedRoute>
                <DefaultRoute />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/welcome" 
            element={
              <ProtectedRoute>
                <WelcomeScreen />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/prescreening/level" 
            element={
              <ProtectedRoute allowedRoles={['employee', 'user']}>
                <PrescreeningLevel />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/prescreening/category" 
            element={
              <ProtectedRoute allowedRoles={['employee', 'user']}>
                <PrescreeningCategory />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/prescreening/tags" 
            element={
              <ProtectedRoute allowedRoles={['employee', 'user']}>
                <PrescreeningTags />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/dashboard" 
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/module/:moduleId" 
            element={
              <ProtectedRoute>
                <TrainingModule />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/quiz/:moduleId" 
            element={
              <ProtectedRoute>
                <Quiz />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/progress" 
            element={
              <ProtectedRoute>
                <Progress />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/calendar" 
            element={
              <ProtectedRoute>
                <TrainingCalendar />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/notifications" 
            element={
              <ProtectedRoute>
                <Notifications />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="/admin" 
            element={
              <ProtectedRoute requiredRole="admin">
                <AdminDashboard />
              </ProtectedRoute>
            } 
          />
        </Routes>
      </main>
      {shouldShowHeader && <Footer />}
      <Analytics />
    </div>
  );
}

export default App;
